// utils/generalizedTopicExtractor.js
import nlp from 'compromise';

/**
 * Extracts the main topic from a text document using a generalized approach
 * that works across domains without hard-coded patterns
 * @param {string} text - The input text
 * @returns {Object} - The main topic and confidence score
 */
export const extractMainTopic = (text) => {
  if (!text || text.trim().length === 0) {
    return { topic: null, confidence: 0 };
  }
  
  console.log("Extracting topic using generalized approach...");
  
  // 1. Prepare the text
  const doc = nlp(text);
  const paragraphs = text.split(/\n\s*\n/);
  
  // 2. Extract potential titles/headers (without domain-specific assumptions)
  const structuralCandidates = extractStructuralElements(text, paragraphs);
  
  // 3. Extract frequent terms, prioritizing capitalized and multi-word phrases
  const frequentTerms = extractFrequentTerms(doc, text);
  
  // 4. Extract meaningful word pairs that appear together
  const coOccurrences = extractCoOccurrences(text);
  
  // 5. Identify potential topics in the introduction
  const introductionCandidates = analyzeIntroduction(paragraphs[0] || '');
  
  // 6. Look for repeated patterns in the document structure
  const repeatedPatterns = findRepeatedPatterns(paragraphs);
  
  // 7. Identify terms that appear in strong contextual positions
  const contextualTerms = identifyContextualTerms(paragraphs);
  
  // 8. Combine all sources of evidence with appropriate weights
  const scoredCandidates = scoreTopicCandidates([
    { items: structuralCandidates, weight: 3 },
    { items: frequentTerms, weight: 2 },
    { items: coOccurrences, weight: 3 },
    { items: introductionCandidates, weight: 4 },
    { items: repeatedPatterns, weight: 3 },
    { items: contextualTerms, weight: 3 }
  ]);
  
  // 9. Return the top candidate and confidence score
  if (scoredCandidates.length > 0) {
    // Ensure reasonable confidence scores
    return {
      topic: scoredCandidates[0].term,
      confidence: Math.min(0.9, Math.max(0.4, scoredCandidates[0].normalizedScore)),
      alternativeTopics: scoredCandidates.slice(1, 3).map(c => c.term)
    };
  }
  
  // Fallback to basic analysis
  const basicAnalysis = doc.match('#Noun+').out('frequency');
  return basicAnalysis.length > 0 
    ? { topic: basicAnalysis[0].normal, confidence: 0.3 }
    : { topic: null, confidence: 0 };
};

/**
 * Extract structural elements like titles and headers
 * Based on formatting, not content
 */
function extractStructuralElements(text, paragraphs) {
  const candidates = [];
  
  // Check for lines that look like titles (short, standalone, no period, etc.)
  const lines = text.split('\n');
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    // Skip empty lines
    if (trimmedLine.length === 0) return;
    
    // Look for potential title patterns
    const isTitleLine = (
      // Short line that doesn't end with period
      (trimmedLine.length < 50 && !trimmedLine.endsWith('.') && trimmedLine.length > 3) ||
      // Line ends with colon (potential section header)
      trimmedLine.endsWith(':') ||
      // Capitalized line (potential header)
      (trimmedLine === trimmedLine.toUpperCase() && trimmedLine.length > 3 && !/^\d+\./.test(trimmedLine))
    );
    
    if (isTitleLine) {
      // Clean up the potential title
      let title = trimmedLine.replace(/:$/, '').trim();
      
      // Skip weak title indicators
      if (title.length > 3 && !isWeakTitle(title)) {
        candidates.push({
          term: title,
          score: 4 + (index === 0 ? 2 : 0) // Boost if it's the first line
        });
      }
    }
  });
  
  // Check if first paragraph might be a title paragraph
  if (paragraphs.length > 0) {
    const firstParagraph = paragraphs[0];
    if (firstParagraph.length < 100 && !firstParagraph.includes('.')) {
      candidates.push({
        term: firstParagraph.trim(),
        score: 4
      });
    }
  }
  
  return candidates;
}

/**
 * Check if a title is a weak indicator
 */
function isWeakTitle(title) {
  // Common weak titles
  const weakTitles = [
    'introduction', 'summary', 'overview', 'conclusion', 
    'background', 'abstract', 'preface'
  ];
  
  // Check for pronouns and articles at the start
  const startsWithWeakWord = /^(The|This|That|These|Those|It|They|I|We|You|Your|My|Our)\b/i.test(title);
  
  // Check if it's a common weak title
  const isCommonWeakTitle = weakTitles.includes(title.toLowerCase());
  
  return startsWithWeakWord || isCommonWeakTitle;
}

/**
 * Extract frequent terms, prioritizing capitalized and multi-word phrases
 */
function extractFrequentTerms(doc, text) {
  const candidates = [];
  
  // Get noun phrases with frequency
  const nounPhrases = doc.match('#Noun+').out('frequency');
  
  // Filter and convert to our format
  nounPhrases.forEach(phrase => {
    // Skip very short phrases
    if (phrase.normal.length <= 2) return;
    
    // Skip common weak terms
    if (isWeakTerm(phrase.normal)) return;
    
    // Calculate base score from frequency
    let score = phrase.count;
    
    // Boost multi-word phrases
    if (phrase.normal.includes(' ')) {
      score *= 1.5;
    }
    
    // Boost capitalized terms
    if (/[A-Z]/.test(phrase.text)) {
      score *= 1.2;
    }
    
    candidates.push({
      term: phrase.normal,
      score: score
    });
  });
  
  // Also look for capitalized terms separately
  const capitalizedRegex = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b/g;
  const capitalizedMatches = [...text.matchAll(capitalizedRegex)].map(m => m[0]);
  
  // Count occurrences of each capitalized term
  const capitalizedCounts = new Map();
  capitalizedMatches.forEach(term => {
    // Skip weak terms
    if (isWeakTerm(term)) return;
    
    capitalizedCounts.set(term, (capitalizedCounts.get(term) || 0) + 1);
  });
  
  // Add capitalized terms to candidates
  capitalizedCounts.forEach((count, term) => {
    candidates.push({
      term: term,
      score: count * 2 // Higher weight for capitalized terms
    });
  });
  
  return candidates;
}

/**
 * Check if a term is a weak indicator
 */
function isWeakTerm(term) {
  const weakTerms = [
    'this', 'that', 'these', 'those', 'it', 'they', 'them', 'their',
    'i', 'me', 'my', 'mine', 'you', 'your', 'yours', 'we', 'us', 'our',
    'the', 'a', 'an', 'some', 'any', 'each', 'every', 'all', 'both',
    'way', 'thing', 'example', 'instance', 'case', 'time', 'place'
  ];
  
  // Check if it's just a single weak term
  const lowerTerm = term.toLowerCase();
  if (weakTerms.includes(lowerTerm)) {
    return true;
  }
  
  // Check if it starts with a weak term
  for (const weakTerm of weakTerms) {
    if (lowerTerm.startsWith(weakTerm + ' ')) {
      return true;
    }
  }
  
  return false;
}

/**
 * Extract meaningful word pairs that appear together
 */
function extractCoOccurrences(text) {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const candidates = [];
  
  // Look for words that frequently appear as a pair
  const wordPairs = new Map();
  
  sentences.forEach(sentence => {
    // Simple tokenization
    const words = sentence.trim()
      .split(/\s+/)
      .filter(w => w.length > 3) // Skip short words
      .filter(w => /^[A-Z][a-z]+$/.test(w)); // Only keep capitalized words
    
    // Count pairs
    for (let i = 0; i < words.length - 1; i++) {
      const pair = `${words[i]} ${words[i+1]}`;
      wordPairs.set(pair, (wordPairs.get(pair) || 0) + 1);
    }
  });
  
  // Convert to candidates
  wordPairs.forEach((count, pair) => {
    if (count > 1 && !isWeakTerm(pair)) {
      candidates.push({
        term: pair,
        score: count * 2
      });
    }
  });
  
  return candidates;
}

/**
 * Analyze introduction paragraph for topic indicators
 */
function analyzeIntroduction(paragraph) {
  if (!paragraph) return [];
  
  const introDoc = nlp(paragraph);
  const candidates = [];
  
  // Get all noun phrases from the first paragraph
  const nounPhrases = introDoc.match('#Noun+').out('array');
  
  // The first noun phrase is often the topic
  if (nounPhrases.length > 0) {
    const firstPhrase = nounPhrases[0];
    if (!isWeakTerm(firstPhrase)) {
      candidates.push({
        term: firstPhrase,
        score: 4
      });
    }
  }
  
  // Look for phrases following "as" in the introduction
  const asPhrases = introDoc.match('as #Noun+').out('array');
  asPhrases.forEach(phrase => {
    // Extract just the noun phrase part
    const nounPart = phrase.replace(/^as\s+/i, '');
    if (nounPart.length > 3 && !isWeakTerm(nounPart)) {
      candidates.push({
        term: nounPart,
        score: 3
      });
    }
  });
  
  // Look for phrases that follow topic-indicating patterns
  const indicatorPatterns = [
    'about', 'focuses on', 'discusses', 'explores', 
    'examines', 'analyzes', 'presents', 'introduces'
  ];
  
  indicatorPatterns.forEach(pattern => {
    const index = paragraph.toLowerCase().indexOf(pattern);
    if (index >= 0) {
      // Get the text after the pattern
      const afterPattern = paragraph.substring(index + pattern.length).trim();
      // Extract the first noun phrase
      const patternDoc = nlp(afterPattern);
      const nounPhrase = patternDoc.match('#Noun+').first().text();
      
      if (nounPhrase && nounPhrase.length > 3 && !isWeakTerm(nounPhrase)) {
        candidates.push({
          term: nounPhrase,
          score: 4
        });
      }
    }
  });
  
  return candidates;
}

/**
 * Find repeated patterns in the document structure
 */
function findRepeatedPatterns(paragraphs) {
  const candidates = [];
  
  // Join all paragraphs for global pattern matching
  const fullText = paragraphs.join(' ');
  
  // Find all "X as Y" patterns (doesn't assume specific X or Y)
  const asPatternRegex = /\b([A-Z][a-z]+)\s+as\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b/g;
  const asPatterns = [...fullText.matchAll(asPatternRegex)];
  
  // Count occurrences of each "X" and "Y" in "X as Y"
  const asTerms = new Map();
  asPatterns.forEach(match => {
    // The "X" in "X as Y"
    const firstTerm = match[1];
    if (!isWeakTerm(firstTerm)) {
      asTerms.set(firstTerm, (asTerms.get(firstTerm) || 0) + 1);
    }
    
    // The "Y" in "X as Y"
    const secondTerm = match[2];
    if (!isWeakTerm(secondTerm)) {
      asTerms.set(secondTerm, (asTerms.get(secondTerm) || 0) + 1);
    }
    
    // The entire pattern "X as Y"
    const fullPattern = match[0];
    asTerms.set(fullPattern, (asTerms.get(fullPattern) || 0) + 2); // Weight full pattern higher
  });
  
  // Convert to candidates
  asTerms.forEach((count, term) => {
    candidates.push({
      term,
      score: count * 1.5
    });
  });
  
  // Find all "X and Y" patterns where X and Y are capitalized
  const andPatternRegex = /\b([A-Z][a-z]+)\s+and\s+([A-Z][a-z]+)\b/g;
  const andPatterns = [...fullText.matchAll(andPatternRegex)];
  
  // Add each "X and Y" pair as a candidate
  andPatterns.forEach(match => {
    candidates.push({
      term: match[0], // Full "X and Y" pattern
      score: 3
    });
  });
  
  return candidates;
}

/**
 * Identify terms that appear in strong contextual positions
 */
function identifyContextualTerms(paragraphs) {
  const candidates = [];
  
  // Terms at the beginning of paragraphs have more weight
  paragraphs.forEach((paragraph, index) => {
    // Skip empty paragraphs
    if (paragraph.trim().length === 0) return;
    
    const sentences = paragraph.split(/[.!?]+/).filter(s => s.trim().length > 0);
    if (sentences.length === 0) return;
    
    // Focus on first sentence of each paragraph
    const firstSentence = sentences[0].trim();
    const sentenceDoc = nlp(firstSentence);
    
    // Get noun phrases from the beginning of the paragraph
    const nounPhrases = sentenceDoc.match('#Noun+').out('array');
    if (nounPhrases.length === 0) return;
    
    // The first noun phrase in a paragraph often indicates the topic
    const firstPhrase = nounPhrases[0];
    if (!isWeakTerm(firstPhrase)) {
      // Additional weight for the first paragraph
      const paraBoost = index === 0 ? 2 : 1;
      candidates.push({
        term: firstPhrase,
        score: 3 * paraBoost
      });
    }
    
    // Look for capitalized terms in the first sentence
    const capitalizedRegex = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\b/g;
    const capitalizedMatches = [...firstSentence.matchAll(capitalizedRegex)].map(m => m[0]);
    
    capitalizedMatches.forEach(term => {
      if (!isWeakTerm(term)) {
        // Additional weight for the first paragraph
        const paraBoost = index === 0 ? 2 : 1;
        candidates.push({
          term,
          score: 3 * paraBoost
        });
      }
    });
  });
  
  return candidates;
}

/**
 * Score and combine all topic candidates
 */
function scoreTopicCandidates(candidateSources) {
  // Combine all candidates with their weighted scores
  const combinedScores = new Map();
  
  candidateSources.forEach(source => {
    source.items.forEach(item => {
      const currentScore = combinedScores.get(item.term) || 0;
      combinedScores.set(item.term, currentScore + (item.score * source.weight));
    });
  });
  
  // Convert to array and sort
  const scoredCandidates = Array.from(combinedScores.entries())
    .map(([term, score]) => ({ term, score }))
    .sort((a, b) => b.score - a.score);
  
  // Normalize scores
  if (scoredCandidates.length > 0) {
    const maxScore = scoredCandidates[0].score;
    scoredCandidates.forEach(candidate => {
      candidate.normalizedScore = candidate.score / maxScore;
    });
  }
  
  return scoredCandidates;
}